import React from 'react';
import classnames from 'classnames';
import TableShowData from '@intelligence/components/TableComponents/components/TableShowData';
import ColumnAligner from '@intelligence/v3/components/TableComponents/ColumnAligner';
import RatioMetricSortBy from '@intelligence/v3/components/TableComponents/RatioMetricSortBy';
import {
  DEFAULT_RATIO_VALUE_CONFIGS,
  getHeaderWidthIntelligence,
  defaultKMBMetricValueFormatter,
  defaultKMBMetricRatioValueFormatter,
} from './utils';
import { LANGUAGE_EN } from '@home/_config/config';

import type { TableColumn } from 'tea-component';
import type { ISortByCommonConfigs } from '@intelligence/v3/components/TableComponents/SortBy';
import type { IDataCompareData, IDataCompareHelper } from '@intelligence/v3/components/DataCompareBubble';
import type { IColumnBaseOption, IRatioValueConfigs } from './utils';

import './kmbMetric.scss';

// KMBMetric Column构建器自定义配置
interface IKMBMetricColumnBuilderOptionCore<T> extends ISortByCommonConfigs<T> {
  // 列单元格的className
  className?: string;
  // 是否展示环比，默认值为true
  showRatio?: boolean;
  // 环比配置
  ratioValueConfigs?: IRatioValueConfigs;
  // 数据源
  dataSource: string;
  // 数据源名
  dataSourceName?: string;
  // 数据源icon
  dataSourceIcon?: string;
  // 指标
  metric: string;
  // 指标名
  metricName?: string;
  // 指标气泡提示
  metricTips?: string | React.ReactNode;
  // 获取指标值
  getValue?: (cvm: T) => string | number | null;
  // 获取指标环比值
  getRatioValue?: (cvm: T) => string | number | null;
  // 获取环比对比气泡数据（按行计算），返回值会传给 TableShowData 的 dataCompareData
  getDataCompareData?: (cvm: T) => (IDataCompareData & IDataCompareHelper) | undefined;
  // 值的格式化方法
  formatter?: (value: string | number | null, cvm: T) => string | number;
  // 环比值格式方法
  ratioValueFormatter?: Function;
  // 排序状态列表
  sorts?: IRatioMetricSortState<T>[] | null;
  // 排序的回调
  onSortsChange?: (sorts: IRatioMetricSortState<T>[]) => void;
}

type IKMBMetricColumnBuilderOption<T> = Partial<TableColumn<T>> &
IColumnBaseOption &
IKMBMetricColumnBuilderOptionCore<T>;

/**
 * IKMBMetricColumn构建器
 * @param kmbMetricColumnBuilderOption IKMBMetricColumn构建器配置
 * @param kmbMetricColumnBuilderOption.className 列单元格的className
 */
const KMBMetricColumnBuilder = <T extends IRecord = IRecord>(
  kmbMetricColumnBuilderOption: IKMBMetricColumnBuilderOption<T>,
): TableColumn<T> => {
  const {
    className,
    showRatio = true,
    ratioValueConfigs,
    locale = LANGUAGE_EN,
    key = '',
    align = 'right',
    width = 100,
    widthCN,
    getValue,
    getRatioValue,
    getDataCompareData,
    formatter = defaultKMBMetricValueFormatter,
    ratioValueFormatter = defaultKMBMetricRatioValueFormatter,
    ...restOption
  } = kmbMetricColumnBuilderOption || {};

  const ratioValueKey = ratioValueConfigs?.key ?? '';
  const ratioValueMaxNumber = ratioValueConfigs?.maxNumber ?? DEFAULT_RATIO_VALUE_CONFIGS.MAX_NUMBER;

  const headerWidth = getHeaderWidthIntelligence(locale, { width, widthCN });

  return {
    key,
    align,
    width: headerWidth,
    header: (
      <ColumnAligner align={align} width={width}>
        <RatioMetricSortBy {...restOption} useRatioSort={showRatio} />
      </ColumnAligner>
    ),
    render: (cvm) => {
      const value = getValue?.(cvm) ?? cvm?.[key];
      const valueNum = Number.parseFloat(value);

      const ratioValue = getRatioValue?.(cvm) ?? cvm?.[ratioValueKey];
      const ratioValueNum = Number.parseFloat(ratioValue);

      const valueFormatted = formatter(valueNum, cvm);
      const ratioValueFormatted = ratioValueFormatter(ratioValueNum, ratioValueConfigs);
      const dataCompareData = getDataCompareData?.(cvm);

      return (
        <ColumnAligner align={align} width={width}>
          <div className={classnames('dbi-common-column-kmb-metric', className)}>
            <TableShowData
              // 是否隐藏环比
              isDodHide={!showRatio}
              // @ts-ignore
              maxNumber={`${ratioValueMaxNumber}+%`}
              // KMB值
              value={valueFormatted}
              // 环比type
              type={ratioValueFormatted?.type}
              // 环比值
              growth={ratioValueFormatted?.value}
              // 环比对比气泡数据
              dataCompareData={dataCompareData}
            />
          </div>
        </ColumnAligner>
      );
    },
  };
};

export default KMBMetricColumnBuilder;
